import { Illustration, Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { type InterviewWithDetails } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useState } from "react";

import { type PlacementCandidate } from "../../../PlacementCandidate/types";
import { useUpdateInterview, type WorkerOutcomeResult } from "../../api/useUpdateInterview";
import { type FeedbackStep } from "./feedbackStep";

interface InterviewAttendanceOptionsContentProps {
  interview: InterviewWithDetails;
  placementCandidate: PlacementCandidate;
  onOptionSelected: (workerOutcome: WorkerOutcomeResult) => void;
  refetchInterviews: () => Promise<unknown>;
  isFetchingInterviews: boolean;
  step: FeedbackStep;
  heading: string;
  options: Array<{ label: string; value: WorkerOutcomeResult }>;
}

export function InterviewAttendanceOptionsContent(props: InterviewAttendanceOptionsContentProps) {
  const {
    interview,
    placementCandidate,
    onOptionSelected,
    refetchInterviews,
    isFetchingInterviews,
    heading,
    options,
    step,
  } = props;

  const eventContext = {
    interview,
    placementCandidate,
    step,
  };

  const [selectedOption, setSelectedOption] = useState<WorkerOutcomeResult>();
  const { mutateAsync: updateInterview, isLoading: isUpdateInterviewLoading } =
    useUpdateInterview();

  useLogEffect(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_OPTIONS_VIEWED, eventContext);

  async function handleCtaClicked(outcomeResult: WorkerOutcomeResult) {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_OPTIONS_CLICKED, {
      ...eventContext,
      action: outcomeResult,
    });
    setSelectedOption(outcomeResult);
    await updateInterview({
      interviewId: interview.id,
      attributes: { workerOutcome: { result: outcomeResult } },
    });
    await refetchInterviews();
    onOptionSelected(outcomeResult);
  }

  return (
    <>
      <Stack
        spacing={8}
        alignItems="center"
        justifyContent="center"
        sx={{ paddingX: 8, paddingY: 6 }}
      >
        <Illustration type="facility" />
        <Stack spacing={6} textAlign="center">
          <Text variant="h2">Thanks for letting us know</Text>
          <Text>{heading}</Text>
        </Stack>
      </Stack>
      <DialogFooter orientation="horizontal" sx={{ px: 8, pb: 8, pt: 2 }}>
        <Stack spacing={4} width="100%">
          {options.map((option) => (
            <LoadingButton
              key={option.value}
              variant="outlined"
              size="large"
              disabled={isUpdateInterviewLoading || isFetchingInterviews}
              isLoading={selectedOption === option.value}
              onClick={async () => {
                await handleCtaClicked(option.value);
              }}
            >
              {option.label}
            </LoadingButton>
          ))}
        </Stack>
      </DialogFooter>
    </>
  );
}
